import React, { useState, useEffect } from 'react';
import { Card, useAPI, Badge } from 'components/lib';
import { NavLink } from 'react-router-dom';
import styles from './CleanupSummary.module.css';

export function CleanupSummary() {
  
  // API calls for cleanup data
  const cleanupData = useAPI('/api/appData/recordsToClean');
  
  const [summaryStats, setSummaryStats] = useState({
    totalIssues: 0,
    errorCount: 0,
    warningCount: 0,
    infoCount: 0,
    triggeredRules: [],
    lastUpdated: null
  });

  useEffect(() => {
    if (cleanupData?.data?.length) {
      const records = cleanupData.data;
      
      const errorCount = records.filter(r => r.status === 'error').length;
      const warningCount = records.filter(r => r.status === 'warning').length;
      const infoCount = records.filter(r => r.status === 'info').length;
      const totalIssues = records.length;
      
      // Calculate triggered rules (monitor rules)
      const ruleStats = {};
      records.forEach(record => {
        if (record.rule_name) {
          if (!ruleStats[record.rule_name]) {
            ruleStats[record.rule_name] = {
              name: record.rule_name,
              count: 0,
              severity: record.status,
              lastTriggered: record.Last_Updated
            };
          }
          ruleStats[record.rule_name].count++;
          // Keep the highest severity
          if (record.status === 'error') ruleStats[record.rule_name].severity = 'error';
          else if (record.status === 'warning' && ruleStats[record.rule_name].severity !== 'error') {
            ruleStats[record.rule_name].severity = 'warning';
          }
        }
      });
      
      const triggeredRules = Object.values(ruleStats)
        .sort((a, b) => {
          // Sort by severity first (error > warning > info), then by count
          const severityOrder = { error: 3, warning: 2, info: 1 };
          if (severityOrder[a.severity] !== severityOrder[b.severity]) {
            return severityOrder[b.severity] - severityOrder[a.severity];
          }
          return b.count - a.count;
        })
        .slice(0, 5);
      
      setSummaryStats({
        totalIssues,
        errorCount,
        warningCount,
        infoCount,
        triggeredRules,
        lastUpdated: records[0]?.Last_Updated || null
      });
    } else {
      setSummaryStats({
        totalIssues: 0,
        errorCount: 0,
        warningCount: 0,
        infoCount: 0,
        triggeredRules: [],
        lastUpdated: null
      });
    }
  }, [cleanupData.data]);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return `${Math.floor(diffInDays / 7)}w ago`;
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error': return '🚨';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '📋';
    }
  };

  return (
    <Card 
      title="Data Monitors" 
      loading={cleanupData?.loading}
      style={{ position: 'relative' }}
    >
      <div className={styles.headerActions}>
        {summaryStats.totalIssues > 0 ? (
          <>
            <NavLink to="/client/logs/cleanup" className={styles.primaryButton}>
              Review & Resolve Issues
            </NavLink>
            <NavLink to="/client/monitors/rules" className={styles.secondaryButton}>
              Manage Monitors
            </NavLink>
          </>
        ) : (
          <NavLink to="/client/monitors/rules" className={styles.secondaryButton}>
            Manage Monitors
          </NavLink>
        )}
      </div>
      
      <div className={styles.cleanupSummaryContent}>

        {/* Stats Row */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{summaryStats.errorCount}</div>
            <div className={styles.statLabel}>Critical Issues</div>
            <div className={styles.statIndicator} style={{background: '#ff4757'}}></div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{summaryStats.warningCount}</div>
            <div className={styles.statLabel}>Warnings</div>
            <div className={styles.statIndicator} style={{background: '#ffa502'}}></div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{summaryStats.infoCount}</div>
            <div className={styles.statLabel}>Info Items</div>
            <div className={styles.statIndicator} style={{background: '#70a1ff'}}></div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{summaryStats.triggeredRules.length}</div>
            <div className={styles.statLabel}>Active Monitors</div>
            <div className={styles.statIndicator} style={{background: '#7bed9f'}}></div>
          </div>
        </div>

        {/* Triggered Rules Section */}
        {summaryStats.triggeredRules.length > 0 && (
          <div className={styles.rulesSection}>
            <h3 className={styles.sectionTitle}>Recently Triggered Monitors</h3>
            <div className={styles.rulesList}>
              {summaryStats.triggeredRules.map((rule, index) => (
                <div key={index} className={`${styles.ruleItem} ${styles[rule.severity]}`}>
                  <div className={styles.ruleIcon}>
                    {getSeverityIcon(rule.severity)}
                  </div>
                  <div className={styles.ruleContent}>
                    <div className={styles.ruleName}>{rule.name}</div>
                    <div className={styles.ruleDetails}>
                      {rule.count} record{rule.count !== 1 ? 's' : ''} flagged
                      {rule.lastTriggered && (
                        <span className={styles.ruleTime}>
                          • {formatTimeAgo(rule.lastTriggered)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.ruleCount}>
                    <Badge 
                      color={rule.severity === 'error' ? 'red' : rule.severity === 'warning' ? 'yellow' : 'blue'} 
                      text={rule.count} 
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </Card>
  );
}